import React from 'react';
import PropTypes from 'prop-types';
import { connectPagination } from 'react-instantsearch-dom';
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleRight, faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import Icon from '../../Icon';

const range = ({ start = 0, end, step = 1 }) => {
  const limitStep = step === 0 ? 1 : step;

  const arrayLength = Math.round(((Number.isNaN(end) ? 1 : end) - start) / limitStep);

  return [...Array(arrayLength)].map((_, current) => (start + current) * limitStep);
};

const calculateSize = (padding, maxPages) => Math.min(2 * padding + 1, maxPages);

const calculatePaddingLeft = (currentPage, padding, maxPages, size) => {
  if (currentPage <= padding) {
    return currentPage;
  }

  if (currentPage >= maxPages - padding) {
    return size - (maxPages - currentPage);
  }

  return padding + 1;
};

const getPages = (currentPage, maxPages, padding) => {
  const size = calculateSize(padding, maxPages);
  if (size === maxPages) return range({ start: 1, end: maxPages + 1 });

  const paddingLeft = calculatePaddingLeft(currentPage, padding, maxPages, size);
  const paddingRight = size - paddingLeft;

  const first = currentPage - paddingLeft;
  const last = currentPage + paddingRight;
  return range({ start: first + 1, end: last + 1 });
};

const Pagination = ({ currentRefinement, nbPages, refine, padding, showNext, showLast, showPrevious, showFirst }) => {
  const lastPage = nbPages;
  const resultsPages = getPages(currentRefinement, nbPages, padding).map(value => ({
    key: value,
    modifier: 'item--page',
    label: value.toString(),
    value,
    selected: value === currentRefinement,
    ariaLabel: value.toString(),
  }));

  let pages = [];
  if (showFirst) {
    pages.push({
      key: 'first',
      modifier: 'item--firstPage',
      disabled: currentRefinement === 1,
      label: <Icon icon={faAngleDoubleLeft} />,
      value: 1,
      ariaLabel: 'ariaFirst',
    });
  }

  if (showPrevious) {
    pages.push({
      key: 'previous',
      modifier: 'item--previousPage',
      disabled: currentRefinement === 1,
      label: <Icon icon={faAngleLeft} />,
      value: currentRefinement - 1,
      ariaLabel: 'ariaPrevious',
    });
  }

  pages = pages.concat(resultsPages);

  if (showNext) {
    pages.push({
      key: 'next',
      modifier: 'item--nextPage',
      disabled: currentRefinement === lastPage || lastPage <= 1,
      label: <Icon icon={faAngleRight} />,
      value: currentRefinement + 1,
      ariaLabel: 'ariaNext',
    });
  }
  if (showLast) {
    pages.push({
      key: 'last',
      modifier: 'item--lastPage',
      disabled: currentRefinement === lastPage || lastPage <= 1,
      label: <Icon icon={faAngleDoubleRight} />,
      value: lastPage,
      ariaLabel: 'ariaLast',
    });
  }

  return (
    <nav aria-label="Search demo pagination">
      <ul className="pagination mb-0">
        {pages.map(page => (
          <li className={clsx('page-item', { disabled: page.disabled, active: page.selected })} key={page.key}>
            <button
              type="button"
              className="page-link"
              onClick={event => {
                event.preventDefault();
                refine(page.value);
              }}
            >
              {page.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

Pagination.propTypes = {
  currentRefinement: PropTypes.number,
  nbPages: PropTypes.number,
  padding: PropTypes.number,
  refine: PropTypes.func,
  showFirst: PropTypes.bool,
  showLast: PropTypes.bool,
  showNext: PropTypes.bool,
  showPrevious: PropTypes.bool,
};

Pagination.defaultProps = {
  currentRefinement: 0,
  nbPages: 0,
  padding: 2,
  refine: undefined,
  showFirst: true,
  showLast: true,
  showNext: true,
  showPrevious: true,
};

export default connectPagination(Pagination);
